import React, { useState, useEffect } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import ReactApexChart from "react-apexcharts";
import { api } from "../services/api";
import ExamResultDetailsLayer from "./ExamResultDetailsLayer";
import { useNavigate } from "react-router-dom";

const ResultsLayer = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("exams"); // 'exams' or 'quizzes'
  const [examResults, setExamResults] = useState([]);
  const [quizResults, setQuizResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedResult, setSelectedResult] = useState(null);
  const [filterCategory, setFilterCategory] = useState("All");
  const [filterStatus, setFilterStatus] = useState("All");
  const [searchTerm, setSearchTerm] = useState("");
  const [showResultDetails, setShowResultDetails] = useState(false);

  // Get current results based on active tab
  const results = activeTab === "exams" ? examResults : quizResults;

  // Fetch results
  useEffect(() => {
    const fetchResults = async () => {
      try {
        setLoading(true);

        // Fetch exam results
        const examResponse = await api.exams.getAllResults();
        if (examResponse.success) {
          const formattedExamResults = formatExamResults(examResponse.data);
          setExamResults(formattedExamResults);
        }

        // Fetch quiz results
        const quizResponse = await api.quizzes.getAllResults();
        if (quizResponse.success) {
          const formattedQuizResults = formatQuizResults(quizResponse.data);
          setQuizResults(formattedQuizResults);
        }

        if (!examResponse.success && !quizResponse.success) {
          setError("Failed to fetch results");
        }
      } catch (err) {
        setError("An error occurred while fetching results: " + err.message);
        console.error("Error fetching results:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchResults();
  }, []);

  // Format exam results from API response
  const formatExamResults = (data) => {
    return data.map((examResult) => {
      // Extract user's name and department info
      const userName = examResult.user
        ? `${examResult.user.firstName} ${examResult.user.lastName}`
        : "Unknown User";

      const userId = examResult.user?.id || "Unknown";

      // Create category breakdowns from the answers
      const breakdown = {};

      if (examResult.answers && examResult.answers.length > 0) {
        // Group questions by category
        examResult.answers.forEach((answer) => {
          const category = answer.question.category?.name || "Uncategorized";

          if (!breakdown[category]) {
            breakdown[category] = { correct: 0, wrong: 0 };
          }

          if (answer.isCorrect) {
            breakdown[category].correct += 1;
          } else {
            breakdown[category].wrong += 1;
          }
        });
      }

      // Format the date
      const examDate = new Date(examResult.completedAt)
        .toISOString()
        .split("T")[0];

      // Determine difficulty level
      const examDifficulty = examResult.exam?.difficulty || "MEDIUM";
      let category = "Basic";
      if (examDifficulty === "MEDIUM") category = "Medium";
      if (examDifficulty === "ADVANCED") category = "Advanced";

      // Calculate total questions, correct and wrong answers
      const totalQuestions = examResult.answers?.length || 0;
      const correctAnswers =
        examResult.answers?.filter((a) => a.isCorrect).length || 0;
      const wrongAnswers = totalQuestions - correctAnswers;

      // Format timeSpent from seconds to minutes
      const minutes = Math.floor(examResult.timeSpent / 60);
      const seconds = examResult.timeSpent % 60;
      const timeSpent = `${minutes} minutes ${seconds} seconds`;

      return {
        id: examResult.id,
        type: "exam",
        name: examResult.exam?.title || "Unknown Exam",
        userName: userName,
        userId: userId,
        department: examResult.user?.department || "Not specified",
        date: examDate,
        category: category,
        score: examResult.score,
        status: examResult.passed ? "Pass" : "Fail",
        timeSpent: timeSpent,
        correctAnswers: correctAnswers,
        wrongAnswers: wrongAnswers,
        totalQuestions: totalQuestions,
        breakdown: breakdown,
        // Keep the original data for reference
        rawData: examResult,
      };
    });
  };

  // Format quiz results from API response
  const formatQuizResults = (data) => {
    return data.map((quizResult) => {
      // Extract user's name and info
      const userName = quizResult.user
        ? `${quizResult.user.firstName} ${quizResult.user.lastName}`
        : "Unknown User";

      const userId = quizResult.user?.id || "Unknown";

      // Create category breakdowns from the answers
      const breakdown = {};

      if (quizResult.answers && quizResult.answers.length > 0) {
        // Group questions by category
        quizResult.answers.forEach((answer) => {
          const category = answer.question.category?.name || "Uncategorized";

          if (!breakdown[category]) {
            breakdown[category] = { correct: 0, wrong: 0 };
          }

          if (answer.isCorrect) {
            breakdown[category].correct += 1;
          } else {
            breakdown[category].wrong += 1;
          }
        });
      }

      // Format the date
      const quizDate = new Date(quizResult.completedAt)
        .toISOString()
        .split("T")[0];

      // Determine difficulty level based on score as quizzes don't have explicit difficulty
      let category = "Basic";
      if (quizResult.score >= 70 && quizResult.score < 85) category = "Medium";
      if (quizResult.score >= 85) category = "Advanced";

      // Calculate total questions, correct and wrong answers
      const totalQuestions = quizResult.answers?.length || 0;
      const correctAnswers =
        quizResult.answers?.filter((a) => a.isCorrect).length || 0;
      const wrongAnswers = totalQuestions - correctAnswers;

      // Format timeSpent from seconds to minutes
      const minutes = Math.floor(quizResult.timeSpent / 60);
      const seconds = quizResult.timeSpent % 60;
      const timeSpent = `${minutes} minutes ${seconds} seconds`;

      return {
        id: quizResult.id,
        type: "quiz",
        name: quizResult.quiz?.title || "Unknown Quiz",
        userName: userName,
        userId: userId,
        department: quizResult.user?.department || "Not specified",
        date: quizDate,
        category: category,
        score: quizResult.score,
        status: quizResult.score >= 70 ? "Pass" : "Fail",
        timeSpent: timeSpent,
        correctAnswers: correctAnswers,
        wrongAnswers: wrongAnswers,
        totalQuestions: totalQuestions,
        breakdown: breakdown,
        // Keep the original data for reference
        rawData: quizResult,
      };
    });
  };

  // Filter functionality
  const filteredResults = results.filter((result) => {
    const matchesCategory =
      filterCategory === "All" || result.category === filterCategory;
    const matchesStatus =
      filterStatus === "All" || result.status === filterStatus;
    const matchesSearch =
      result.userName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      result.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      result.userId.toLowerCase().includes(searchTerm.toLowerCase());

    return matchesCategory && matchesStatus && matchesSearch;
  });

  // View result details - updated to handle both navigation and internal state
  const viewResultDetails = (result) => {
    if (result.type === "exam") {
      // For exam results, use the dedicated ExamResultDetailsPage via navigation
      navigate(`/exam-result-details?resultId=${result.id}`);
    } else {
      // For quiz results, keep using the current implementation
      setSelectedResult(result);
      setShowResultDetails(true);
    }
  };

  // Export functionality
  const exportToPDF = (resultId) => {
    alert(`Exporting result #${resultId} to PDF...`);
    // In a real app, this would trigger a PDF generation and download
  };

  const exportToExcel = (resultId) => {
    alert(`Exporting result #${resultId} to Excel...`);
    // In a real app, this would trigger an Excel generation and download
  };

  // Charts for result analysis
  const getDonutChartOptions = (title) => {
    return {
      chart: {
        type: "donut",
      },
      legend: {
        position: "bottom",
      },
      responsive: [
        {
          breakpoint: 480,
          options: {
            chart: {
              width: 200,
            },
            legend: {
              position: "bottom",
            },
          },
        },
      ],
      title: {
        text: title,
        align: "center",
        style: {
          fontSize: "16px",
          fontWeight: "bold",
        },
      },
      plotOptions: {
        pie: {
          donut: {
            labels: {
              show: true,
              total: {
                show: true,
                label: "Total",
                formatter: function (w) {
                  const total = w.globals.seriesTotals.reduce(
                    (a, b) => a + b,
                    0
                  );
                  return total;
                },
              },
            },
          },
        },
      },
      labels: ["Correct", "Wrong"],
      colors: ["#28a745", "#dc3545"],
    };
  };

  const getBarChartOptions = () => {
    return {
      chart: {
        type: "bar",
        stacked: true,
        toolbar: {
          show: false,
        },
      },
      plotOptions: {
        bar: {
          horizontal: false,
          columnWidth: "55%",
        },
      },
      dataLabels: {
        enabled: false,
      },
      stroke: {
        show: true,
        width: 2,
        colors: ["transparent"],
      },
      xaxis: {
        categories: selectedResult ? Object.keys(selectedResult.breakdown) : [],
      },
      yaxis: {
        title: {
          text: "Number of Questions",
        },
      },
      fill: {
        opacity: 1,
      },
      legend: {
        position: "bottom",
      },
      colors: ["#28a745", "#dc3545"],
    };
  };

  if (loading) {
    return (
      <div className="container-fluid">
        <div className="row">
          <div className="col-12 text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-3">Loading results...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container-fluid">
        <div className="row">
          <div className="col-12">
            <div className="alert alert-danger" role="alert">
              <h4 className="alert-heading">Error!</h4>
              <p>{error}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid">
      {!showResultDetails ? (
        <>
          {/* Results listing page */}
          <div className="row mb-4">
            <div className="col-12">
              <div className="card bg-base">
                <div className="card-body p-16">
                  {/* Tabs for switching between exams and quizzes */}
                  <ul className="nav nav-tabs mb-4">
                    <li className="nav-item">
                      <button
                        className={`nav-link ${
                          activeTab === "exams" ? "active" : ""
                        }`}
                        onClick={() => setActiveTab("exams")}
                      >
                        <Icon
                          icon="mdi:clipboard-text-outline"
                          className="me-2"
                        />
                        Exam Results ({examResults.length})
                      </button>
                    </li>
                    <li className="nav-item">
                      <button
                        className={`nav-link ${
                          activeTab === "quizzes" ? "active" : ""
                        }`}
                        onClick={() => setActiveTab("quizzes")}
                      >
                        <Icon icon="mdi:help-circle-outline" className="me-2" />
                        Quiz Results ({quizResults.length})
                      </button>
                    </li>
                  </ul>

                  <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-3">
                    <h5 className="fw-semibold mb-0">
                      {activeTab === "exams" ? "Exam Results" : "Quiz Results"}
                    </h5>
                    <div className="d-flex align-items-center gap-2 flex-wrap">
                      <div className="position-relative">
                        <input
                          type="text"
                          className="form-control pe-4"
                          placeholder="Search by name, exam or ID..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                        />
                        <Icon
                          icon="heroicons:magnifying-glass"
                          className="position-absolute end-0 top-50 translate-middle-y me-2 text-secondary-light"
                          style={{ pointerEvents: "none" }}
                        />
                      </div>
                      <select
                        className="form-select w-auto"
                        value={filterCategory}
                        onChange={(e) => setFilterCategory(e.target.value)}
                      >
                        <option value="All">All Categories</option>
                        <option value="Basic">Basic</option>
                        <option value="Medium">Medium</option>
                        <option value="Advanced">Advanced</option>
                      </select>
                      <select
                        className="form-select w-auto"
                        value={filterStatus}
                        onChange={(e) => setFilterStatus(e.target.value)}
                      >
                        <option value="All">All Status</option>
                        <option value="Pass">Pass</option>
                        <option value="Fail">Fail</option>
                      </select>
                    </div>
                  </div>
                  <div className="row mb-3 text-center">
                    <div className="col-md-3 col-sm-6 mb-3">
                      <div className="card bg-primary-50 border-0">
                        <div className="card-body">
                          <h6 className="mb-1 fw-normal text-primary-light">
                            Total {activeTab === "exams" ? "Exams" : "Quizzes"}{" "}
                            Taken
                          </h6>
                          <h3 className="mb-0 fw-bold">{results.length}</h3>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-3 col-sm-6 mb-3">
                      <div className="card bg-success-50 border-0">
                        <div className="card-body">
                          <h6 className="mb-1 fw-normal text-success-main">
                            Pass Rate
                          </h6>
                          <h3 className="mb-0 fw-bold">
                            {results.length
                              ? Math.round(
                                  (results.filter((r) => r.status === "Pass")
                                    .length /
                                    results.length) *
                                    100
                                )
                              : 0}
                            %
                          </h3>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-3 col-sm-6 mb-3">
                      <div className="card bg-warning-50 border-0">
                        <div className="card-body">
                          <h6 className="mb-1 fw-normal text-warning-main">
                            Average Score
                          </h6>
                          <h3 className="mb-0 fw-bold">
                            {results.length
                              ? Math.round(
                                  results.reduce((acc, r) => acc + r.score, 0) /
                                    results.length
                                )
                              : 0}
                            %
                          </h3>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-3 col-sm-6 mb-3">
                      <div className="card bg-danger-50 border-0">
                        <div className="card-body">
                          <h6 className="mb-1 fw-normal text-danger-main">
                            Fail Rate
                          </h6>
                          <h3 className="mb-0 fw-bold">
                            {results.length
                              ? Math.round(
                                  (results.filter((r) => r.status === "Fail")
                                    .length /
                                    results.length) *
                                    100
                                )
                              : 0}
                            %
                          </h3>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="table-responsive">
                    <table className="table table-striped table-hover">
                      <thead className="bg-neutral-50 dark:bg-neutral-900">
                        <tr>
                          <th>User</th>
                          <th>
                            {activeTab === "exams" ? "Exam Name" : "Quiz Name"}
                          </th>
                          <th>Date</th>
                          <th>Category</th>
                          <th>Score</th>
                          <th>Status</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredResults.length === 0 ? (
                          <tr>
                            <td colSpan="7" className="text-center py-3">
                              No results found matching your criteria.
                            </td>
                          </tr>
                        ) : (
                          filteredResults.map((result) => (
                            <tr key={result.id}>
                              <td>
                                <div>
                                  <h6 className="fw-semibold mb-1">
                                    {result.userName}
                                  </h6>
                                  <div className="text-secondary-light small">
                                    {result.userId} • {result.department}
                                  </div>
                                </div>
                              </td>
                              <td>{result.name}</td>
                              <td>
                                {new Date(result.date).toLocaleDateString()}
                              </td>
                              <td>
                                <span
                                  className={`badge ${
                                    result.category === "Basic"
                                      ? "bg-info"
                                      : result.category === "Medium"
                                      ? "bg-warning"
                                      : "bg-danger"
                                  }`}
                                >
                                  {result.category}
                                </span>
                              </td>
                              <td>
                                <div className="d-flex align-items-center">
                                  <div
                                    className="progress flex-grow-1 me-2"
                                    style={{ height: "6px" }}
                                  >
                                    <div
                                      className={`progress-bar ${
                                        result.score >= 70
                                          ? "bg-success"
                                          : "bg-danger"
                                      }`}
                                      style={{ width: `${result.score}%` }}
                                    ></div>
                                  </div>
                                  <span>{result.score}%</span>
                                </div>
                              </td>
                              <td>
                                <span
                                  className={`badge ${
                                    result.status === "Pass"
                                      ? "bg-success"
                                      : "bg-danger"
                                  }`}
                                >
                                  {result.status}
                                </span>
                              </td>
                              <td>
                                <div className="d-flex gap-2">
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-outline-primary"
                                    onClick={() => viewResultDetails(result)}
                                  >
                                    <Icon icon="mdi:eye-outline" /> View
                                  </button>
                                  <div className="dropdown">
                                    <button
                                      className="btn btn-sm btn-outline-secondary dropdown-toggle"
                                      type="button"
                                      data-bs-toggle="dropdown"
                                    >
                                      Export
                                    </button>
                                    <ul className="dropdown-menu">
                                      <li>
                                        <button
                                          className="dropdown-item"
                                          onClick={() => exportToPDF(result.id)}
                                        >
                                          <Icon
                                            icon="mdi:file-pdf-box"
                                            className="me-2"
                                          />
                                          Export as PDF
                                        </button>
                                      </li>
                                      <li>
                                        <button
                                          className="dropdown-item"
                                          onClick={() =>
                                            exportToExcel(result.id)
                                          }
                                        >
                                          <Icon
                                            icon="mdi:microsoft-excel"
                                            className="me-2"
                                          />
                                          Export as Excel
                                        </button>
                                      </li>
                                    </ul>
                                  </div>
                                </div>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      ) : (
        <>
          {/* For quiz results, keep using the existing implementation */}
          {selectedResult && selectedResult.type === "quiz" && (
            <>
              <div className="row mb-4">
                <div className="col-12 mb-4">
                  <button
                    type="button"
                    className="btn btn-outline-primary"
                    onClick={() => setShowResultDetails(false)}
                  >
                    <Icon icon="mdi:arrow-left" className="me-1" /> Back to
                    Results
                  </button>
                </div>

                <div className="col-12">
                  <div className="card bg-base">
                    <div className="card-header bg-base border-bottom">
                      <div className="d-flex flex-wrap justify-content-between align-items-center">
                        <h5 className="mb-0 fw-semibold">
                          {selectedResult.type === "exam" ? "Exam" : "Quiz"}{" "}
                          Result Details
                        </h5>
                        <div className="d-flex gap-2">
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-primary"
                            onClick={() => exportToPDF(selectedResult.id)}
                          >
                            <Icon icon="mdi:file-pdf-box" className="me-1" />{" "}
                            Export as PDF
                          </button>
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-success"
                            onClick={() => exportToExcel(selectedResult.id)}
                          >
                            <Icon icon="mdi:microsoft-excel" className="me-1" />{" "}
                            Export as Excel
                          </button>
                        </div>
                      </div>
                    </div>
                    <div className="card-body">
                      <div className="row mb-4">
                        <div className="col-lg-6">
                          <table className="table table-borderless">
                            <tbody>
                              <tr>
                                <th className="ps-0" style={{ width: "200px" }}>
                                  Name:
                                </th>
                                <td>{selectedResult.userName}</td>
                              </tr>
                              <tr>
                                <th className="ps-0">Employee ID:</th>
                                <td>{selectedResult.userId}</td>
                              </tr>
                              <tr>
                                <th className="ps-0">Department:</th>
                                <td>{selectedResult.department}</td>
                              </tr>
                              <tr>
                                <th className="ps-0">
                                  {selectedResult.type === "exam"
                                    ? "Exam"
                                    : "Quiz"}
                                  :
                                </th>
                                <td>{selectedResult.name}</td>
                              </tr>
                              <tr>
                                <th className="ps-0">Date Taken:</th>
                                <td>
                                  {new Date(
                                    selectedResult.date
                                  ).toLocaleDateString()}
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                        <div className="col-lg-6">
                          <table className="table table-borderless">
                            <tbody>
                              <tr>
                                <th className="ps-0" style={{ width: "200px" }}>
                                  Category:
                                </th>
                                <td>
                                  <span
                                    className={`badge ${
                                      selectedResult.category === "Basic"
                                        ? "bg-info"
                                        : selectedResult.category === "Medium"
                                        ? "bg-warning"
                                        : "bg-danger"
                                    }`}
                                  >
                                    {selectedResult.category}
                                  </span>
                                </td>
                              </tr>
                              <tr>
                                <th className="ps-0">Score:</th>
                                <td>
                                  <span
                                    className={`badge ${
                                      selectedResult.score >= 70
                                        ? "bg-success"
                                        : "bg-danger"
                                    }`}
                                  >
                                    {selectedResult.score}%
                                  </span>
                                </td>
                              </tr>
                              <tr>
                                <th className="ps-0">Status:</th>
                                <td>
                                  <span
                                    className={`badge ${
                                      selectedResult.status === "Pass"
                                        ? "bg-success"
                                        : "bg-danger"
                                    }`}
                                  >
                                    {selectedResult.status}
                                  </span>
                                </td>
                              </tr>
                              <tr>
                                <th className="ps-0">Time Spent:</th>
                                <td>{selectedResult.timeSpent}</td>
                              </tr>
                              <tr>
                                <th className="ps-0">Questions:</th>
                                <td>
                                  {selectedResult.totalQuestions} (
                                  {selectedResult.correctAnswers} correct,{" "}
                                  {selectedResult.wrongAnswers} incorrect)
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>

                      <div className="row">
                        <div className="col-lg-5">
                          <div className="card bg-neutral-50 dark:bg-neutral-900 border">
                            <div className="card-body">
                              <ReactApexChart
                                options={getDonutChartOptions(
                                  "Overall Performance"
                                )}
                                series={[
                                  selectedResult.correctAnswers,
                                  selectedResult.wrongAnswers,
                                ]}
                                type="donut"
                                height={300}
                              />
                            </div>
                          </div>
                        </div>
                        <div className="col-lg-7">
                          <div className="card bg-neutral-50 dark:bg-neutral-900 border">
                            <div className="card-body">
                              <h6 className="fw-semibold mb-3 text-center">
                                Performance by Question Category
                              </h6>
                              <ReactApexChart
                                options={getBarChartOptions()}
                                series={[
                                  {
                                    name: "Correct Answers",
                                    data: Object.values(
                                      selectedResult.breakdown
                                    ).map((value) => value.correct),
                                  },
                                  {
                                    name: "Wrong Answers",
                                    data: Object.values(
                                      selectedResult.breakdown
                                    ).map((value) => value.wrong),
                                  },
                                ]}
                                type="bar"
                                height={300}
                              />
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="row mt-4">
                        <div className="col-12">
                          <div className="card bg-neutral-50 dark:bg-neutral-900 border">
                            <div className="card-header bg-neutral-50 dark:bg-neutral-900 border-bottom">
                              <h6 className="fw-semibold mb-0">
                                Performance Analysis
                              </h6>
                            </div>
                            <div className="card-body">
                              <div className="table-responsive">
                                <table className="table">
                                  <thead>
                                    <tr>
                                      <th>Question Category</th>
                                      <th>Total Questions</th>
                                      <th>Correct</th>
                                      <th>Wrong</th>
                                      <th>Score</th>
                                      <th>Status</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {Object.entries(
                                      selectedResult.breakdown
                                    ).map(([category, data]) => {
                                      const total = data.correct + data.wrong;
                                      const score = Math.round(
                                        (data.correct / total) * 100
                                      );

                                      return (
                                        <tr key={category}>
                                          <td>{category}</td>
                                          <td>{total}</td>
                                          <td>{data.correct}</td>
                                          <td>{data.wrong}</td>
                                          <td>
                                            <div className="d-flex align-items-center">
                                              <div
                                                className="progress flex-grow-1 me-2"
                                                style={{ height: "6px" }}
                                              >
                                                <div
                                                  className={`progress-bar ${
                                                    score >= 70
                                                      ? "bg-success"
                                                      : "bg-danger"
                                                  }`}
                                                  style={{ width: `${score}%` }}
                                                ></div>
                                              </div>
                                              <span>{score}%</span>
                                            </div>
                                          </td>
                                          <td>
                                            <span
                                              className={`badge ${
                                                score >= 70
                                                  ? "bg-success"
                                                  : "bg-danger"
                                              }`}
                                            >
                                              {score >= 70 ? "Pass" : "Fail"}
                                            </span>
                                          </td>
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="mt-4">
                        <h6 className="fw-semibold mb-3">Recommendations</h6>
                        <div className="card bg-light border-0 p-3">
                          <ul className="mb-0">
                            {selectedResult.score < 70 && (
                              <li className="text-danger mb-2">
                                Overall score is below passing threshold.
                                Recommendation: Retake the{" "}
                                {selectedResult.type === "exam"
                                  ? "exam"
                                  : "quiz"}{" "}
                                after reviewing course materials.
                              </li>
                            )}
                            {Object.entries(selectedResult.breakdown).map(
                              ([category, data]) => {
                                const total = data.correct + data.wrong;
                                const score = Math.round(
                                  (data.correct / total) * 100
                                );

                                if (score < 70) {
                                  return (
                                    <li key={category} className="mb-2">
                                      Focus on improving knowledge in{" "}
                                      <strong>{category}</strong> section where
                                      performance was {score}%.
                                    </li>
                                  );
                                }
                                return null;
                              }
                            )}
                            {selectedResult.score >= 70 && (
                              <li className="text-success">
                                Congratulations on passing! To further improve,
                                review the specific questions that were answered
                                incorrectly.
                              </li>
                            )}
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
};

export default ResultsLayer;
